import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  useSearchParams,
  useNavigate,
  createSearchParams,
} from "react-router-dom";

import ProductCard from "./ProductCard";
import ProductCardMobile from "./ProductCardMobile";
import SearchModal from "./SearchModal";
import ProductControls from "./ProductControls";
import LoadingIndicator from "./LoadingIndicator";
import { getProducts } from "../services/api";
import { Helmet } from "react-helmet-async";
import "./ProductGrid.css";

const MOBILE_BREAKPOINT = 768;

const ProductGrid = ({ windowSize }) => {
  /* ────────────────────────── 새 URL 훅 ────────────────────────── */
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  /* ────────────────────────── 상태 ────────────────────────── */
  const [products, setProducts] = useState([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortOption, setSortOption] = useState("revenue");

  const [searchTerm, setSearchTerm] = useState("");
  const [typedSearchTerm, setTypedSearchTerm] = useState("");
  const [showSearchModal, setShowSearchModal] = useState(false);

  const [categories, setCategories] = useState({ c1: "", c2: "", c3: "" });
  const [onlyKorean, setOnlyKorean] = useState(false);
  const [onlyPromo, setOnlyPromo] = useState(false);

  const observerRef = useRef(null);
  const isMobile = (windowSize || window.innerWidth) < MOBILE_BREAKPOINT;

  /* ────────────────────────── URL → 상태 초기화 (1회) ────────────────────────── */
  useEffect(() => {
    setCategories({
      c1: searchParams.get("c1") || "",
      c2: searchParams.get("c2") || "",
      c3: searchParams.get("c3") || "",
    });
  }, [searchParams]); // only on mount

  /* ────────────────────────── URL 동기화 헬퍼 ────────────────────────── */
  const syncUrl = (cats) => {
    const params = {};
    if (cats.c1) params.c1 = cats.c1;
    if (cats.c2) params.c2 = cats.c2;
    if (cats.c3) params.c3 = cats.c3;

    navigate(
      { pathname: "/", search: `?${createSearchParams(params)}` },
      { replace: true }
    );
  };

  /* ────────────────────────── 데이터 로드 ────────────────────────── */
  const loadProducts = () => {
    setLoading(true);
    setError(null);
    getProducts()
      .then((data) => {
        setProducts(data || []);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadProducts();
  }, []);

  /* ────────────────────────── 스크롤 관찰 ────────────────────────── */
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => entry.isIntersecting && setPage((p) => p + 1),
      { threshold: 1.0 }
    );
    observerRef.current && observer.observe(observerRef.current);
    return () => observer.disconnect();
  }, []);

  /* ────────────────────────── 필터 + 정렬 (메모이제이션) ────────────────────────── */
  const PAGE_SIZE = 20;

  const filteredProducts = useMemo(() => {
    return (products || [])
      .filter((p) =>
        searchTerm
          ? p.product_title.toLowerCase().includes(searchTerm.toLowerCase())
          : true
      )
      .filter((p) =>
        categories.c1 ? p.first_level_category_name === categories.c1 : true
      )
      .filter((p) =>
        categories.c2 ? p.second_level_category_name === categories.c2 : true
      )
      .filter((p) =>
        categories.c3 ? p.keywords?.split(",").includes(categories.c3) : true
      )
      .filter((p) =>
        onlyKorean ? p.original_price_currency === "KRW" : true
      )
      .filter((p) =>
        onlyPromo ? p.promo_code_info && p.promo_code_info !== "nan" : true
      )
      .sort((a, b) => {
        if (sortOption === "lastest_volume") return b.lastest_volume - a.lastest_volume;
        if (sortOption === "volume_diff_today_yesterday")
          return b.volume_diff_today_yesterday - a.volume_diff_today_yesterday;
        return b.revenue - a.revenue;
      });
  }, [products, searchTerm, categories, sortOption, onlyKorean, onlyPromo]);

  const visibleProducts = useMemo(
    () => filteredProducts.slice(0, page * PAGE_SIZE),
    [filteredProducts, page]
  );

  /* ────────────────────────── 카테고리 변경 ────────────────────────── */
  const handleCategoryChange = (level, value) => {
    setSearchTerm("");
    const next = {
      c1: level === "c1" ? value : "",
      c2: level === "c2" ? value : "",
      c3: level === "c3" ? value : "",
    };
    setCategories(next);
    setPage(1);
    syncUrl(next);
  };

  const handleTagClick = (tag, categoryType) => {
    setSearchTerm("");
    const next = {
      c1: categoryType === "c1" ? (categories.c1 === tag ? "" : tag) : "",
      c2: categoryType === "c2" ? (categories.c2 === tag ? "" : tag) : "",
      c3: categoryType === "c3" ? (categories.c3 === tag ? "" : tag) : "",
    };
    setCategories(next);
    setPage(1);
    syncUrl(next);
  };

  /* ────────────────────────── 검색 ────────────────────────── */
  const handleSearch = (term) => {
    setSearchTerm((prev) => (prev === term ? "" : term));
    const cleared = { c1: "", c2: "", c3: "" };
    setCategories(cleared);
    setShowSearchModal(false);
    setPage(1);
    syncUrl(cleared);
  };

  /* ────────────────────────── JSX ────────────────────────── */
  return (
    <div className="product-container">
      <Helmet>
        <title>Picklite - Smart Shopping</title>
        <meta name="description" content="Picklite에서 인기 상품을 비교하고 최저가로 구매하세요." />
      </Helmet>
      <ProductControls
        /* 정렬 */
        sortOption={sortOption}
        onSortOptionChange={(e) => {
          setSortOption(e.target.value);
          setPage(1);
        }}
        /* 검색 */
        onOpenSearch={() => setShowSearchModal(true)}
        searchTerm={searchTerm}
        handleSearch={handleSearch}
        /* 카테고리 */
        selectedCategory1={categories.c1}
        selectedCategory2={categories.c2}
        selectedCategory3={categories.c3}
        onCategory1Change={(e) => handleCategoryChange("c1", e.target.value)}
        onCategory2Change={(e) => handleCategoryChange("c2", e.target.value)}
        onCategory3Change={(e) => handleCategoryChange("c3", e.target.value)}
        /* 토글 */
        onlyKorean={onlyKorean}
        onToggleOnlyKorean={() => {
          setOnlyKorean(!onlyKorean);
          setPage(1);
        }}
        onlyPromo={onlyPromo}
        onToggleOnlyPromo={() => {
          setOnlyPromo(!onlyPromo);
          setPage(1);
        }}
        /* 전체 데이터 */
        products={products || []}
      />

      <p className="referral-notice">
        하위 제휴 링크를 통해 구매하면, 개발자에게 소정의 수익이 제공됩니다.
      </p>

      <div className="product-grid">
        {error ? (
          <div className="error-state">
            <p>데이터를 불러오지 못했습니다: {error}</p>
            <button onClick={loadProducts}>다시 시도</button>
          </div>
        ) : loading ? (
          <LoadingIndicator loading={loading} />
        ) : visibleProducts.length ? (
          visibleProducts.map((p, i) =>
            isMobile ? (
              <ProductCardMobile
                key={p.id || i}
                product={p}
                onTagClick={handleTagClick}
                selectedCategory1={categories.c1}
                selectedCategory2={categories.c2}
                selectedCategory3={categories.c3}
              />
            ) : (
              <ProductCard
                key={p.id || i}
                product={p}
                onTagClick={handleTagClick}
                selectedCategory1={categories.c1}
                selectedCategory2={categories.c2}
                selectedCategory3={categories.c3}
              />
            )
          )
        ) : (
          <p>😥 검색 결과가 없습니다.</p>
        )}
      </div>

      {/* 무한 스크롤 센티널 */}
      <div ref={observerRef} style={{ height: "20px" }} />

      {/* 검색 모달 */}
      {showSearchModal && (
        <SearchModal
          typedSearchTerm={typedSearchTerm}
          setTypedSearchTerm={setTypedSearchTerm}
          onSearch={() => handleSearch(typedSearchTerm)}
          onClose={() => setShowSearchModal(false)}
          onKeyDown={(e) => e.key === "Enter" && handleSearch(typedSearchTerm)}
        />
      )}
    </div>
  );
};

export default ProductGrid;
